import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  ButtonBase,
  Button,
  Chip,
  Slider,
  TextField,
  FormControl,
  FormLabel,
  Fade,
  LinearProgress,
  Alert,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  RadioButtonUncheckedOutlined as LowEngagementIcon,
  AdjustOutlined as OkayEngagementIcon,
  TripOriginOutlined as GoodEngagementIcon,
  LensOutlined as GreatEngagementIcon,
  StarsOutlined as FullEngagementIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import StyledButton from '../UI/StyledButton';
import LogFormShell from '../UI/LogFormShell';
import LogTimeField from '../UI/LogTimeField';
import SimpleNotesInput from '../UI/SimpleNotesInput';
import DateTimeSection from '../Dashboard/Incidents/QuickCapture/DateTimeSection';
import { getDailyCareConfig } from './dailyCareConfig';
import {
  ACTIVITY_TYPE_OPTIONS,
  getActivityTheme,
  getPrimaryActivityTheme,
} from '../../constants/activityThemes';
import { saveDailyCareEntry } from '../../services/dailyCareService';
import { useAsyncForm } from '../../hooks/useAsyncForm';
import { db } from '../../services/firebase';
import { collection, addDoc, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const getDefaultFormData = (fields = []) => fields.reduce((acc, field) => {
  if (field.defaultValue !== undefined) {
    acc[field.key] = field.defaultValue;
  }
  return acc;
}, {});

const getEntryContextText = (entry = {}) => String(
  entry.note
  || entry.notes
  || entry.sourceText
  || entry.originalData?.sourceText
  || entry.source?.body
  || entry.originalData?.source?.body
  || entry.content
  || entry.originalData?.content
  || entry.text
  || entry.originalData?.text
  || entry.primaryText
  || entry.originalData?.primaryText
  || entry.label
  || entry.originalData?.label
  || entry.titlePrefix
  || entry.originalData?.titlePrefix
  || entry.title
  || entry.originalData?.title
  || ''
).trim();

const ACTIVITY_ENGAGEMENT_OPTIONS = [
  { value: 1, label: 'Low', iconComponent: LowEngagementIcon },
  { value: 2, label: 'Okay', iconComponent: OkayEngagementIcon },
  { value: 3, label: 'Good', iconComponent: GoodEngagementIcon },
  { value: 4, label: 'Great', iconComponent: GreatEngagementIcon },
  { value: 5, label: 'Fully engaged', iconComponent: FullEngagementIcon },
];

const renderOptionVisual = (option, {
  iconSize = 15,
  emojiFontSize = '0.92rem',
  color = 'inherit',
  opacity = 0.95,
} = {}) => {
  if (option?.iconComponent) {
    const IconComponent = option.iconComponent;
    return <IconComponent sx={{ fontSize: iconSize, lineHeight: 1, flexShrink: 0, color }} />;
  }

  return option?.emoji ? (
    <Typography component="span" sx={{ fontSize: emojiFontSize, lineHeight: 1, flexShrink: 0, opacity }}>
      {option.emoji}
    </Typography>
  ) : null;
};

const padTwo = (value) => String(value).padStart(2, '0');

const toLocalDateKey = (date) => `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;

const toLocalTimeKey = (date) => `${padTwo(date.getHours())}:${padTwo(date.getMinutes())}`;

const getEngagementLabel = (value) => (
  ACTIVITY_ENGAGEMENT_OPTIONS.find((option) => Number(option.value) === Number(value))?.label || null
);

const DailyCareModal = ({
  open,
  onClose,
  child,
  actionType,
  onComplete,
  mode = 'create',
  initialData = null,
  entryId = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [engagementValue, setEngagementValue] = useState(null);
  const [activityTimestamp, setActivityTimestamp] = useState(() => new Date());
  const isSinglePageFlow = actionType === 'activity';
  const isEditMode = mode === 'edit' || Boolean(entryId || initialData?.id);

  // Get configuration for the specific action type
  const config = getDailyCareConfig(actionType, child);
  // Use async form hook for daily care entry submission
  const careForm = useAsyncForm({
    onSuccess: (result) => {
      onComplete?.(actionType, result);
      onClose();
    },
    validate: (data) => {
      const fieldsToValidate = isSinglePageFlow
        ? (config?.fields || []).filter((field) => field.required)
        : [config?.fields?.[currentStep]].filter(Boolean).filter((field) => field.required);

      for (const field of fieldsToValidate) {
        const value = data.formData?.[field.key];
        if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          throw new Error(`${field.label} is required`);
        }
      }
    }
  });

  useEffect(() => {
    // Reset form when action type changes
    setCurrentStep(0);
    const defaultFormData = getDefaultFormData(config?.fields || []);
    if (isEditMode && initialData) {
      const editData = actionType === 'activity'
        ? (
            initialData.activityDetails
            || initialData.originalData?.activityDetails
            || initialData.data
            || initialData.originalData
            || initialData
            || {}
          )
        : (initialData.data || initialData.originalData || initialData || {});
      const sourceBody = getEntryContextText(initialData);
      const resolvedTimestamp = initialData.timestamp?.toDate
        ? initialData.timestamp.toDate()
        : (initialData.timestamp ? new Date(initialData.timestamp) : new Date());
      const resolvedEditData = sourceBody
        ? {
            ...editData,
            notes: editData.notes || sourceBody,
          }
        : editData;

      setFormData({
        ...defaultFormData,
        ...resolvedEditData,
      });
      setEngagementValue(resolvedEditData.engagement ?? resolvedEditData.level ?? defaultFormData.engagement ?? null);
      setActivityTimestamp(resolvedTimestamp);
      careForm.reset();
      return;
    }

    setFormData(defaultFormData);
    setEngagementValue(defaultFormData.engagement ?? null);
    setActivityTimestamp(new Date());
    careForm.reset();
  }, [actionType, open, initialData, isEditMode]); // Remove careForm from dependencies to prevent infinite loop

  // Define handleClose first to avoid circular dependency
  const handleClose = () => {
    setCurrentStep(0);
    setFormData(getDefaultFormData(config?.fields || []));
    setActivityTimestamp(new Date());
    careForm.reset();
    onClose();
  };

  // Always render the same structure to maintain consistent hooks
  if (!config || !open) {
    return (
      <Modal open={false} onClose={handleClose}>
        <Box />
      </Modal>
    );
  }

  const currentField = config.fields[currentStep];
  const progress = ((currentStep + 1) / config.fields.length) * 100;
  const isLastStep = currentStep === config.fields.length - 1;
  const fieldsToRender = isSinglePageFlow ? config.fields : [currentField];
  const handleFieldChange = (fieldKey, value) => {
    if (fieldKey === 'engagement') {
      setEngagementValue(Number(value));
    }
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const isValueFilled = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
  };

  const canProceed = () => {
    if (isSinglePageFlow) {
      return config.fields.every((field) => !field.required || isValueFilled(formData[field.key]));
    }

    if (!currentField.required) return true;
    return isValueFilled(formData[currentField.key]);
  };

  const handleSave = () => {
    careForm.submitForm(
      async () => {
        const primaryActivityTheme = getPrimaryActivityTheme(formData.activityTypes || []);
        const resolvedTimestamp = actionType === 'activity' ? activityTimestamp : new Date();
        const resolvedActivityData = actionType === 'activity'
          ? {
              activityTypes: Array.isArray(formData.activityTypes)
                ? formData.activityTypes
                : [formData.activityTypes].filter(Boolean),
              activityType: Array.isArray(formData.activityTypes)
                ? (formData.activityTypes[0] || null)
                : (formData.activityTypes || null),
              engagement: engagementValue ?? formData.engagement ?? null,
              engagementLabel: getEngagementLabel(engagementValue ?? formData.engagement ?? null),
              notes: String(formData.notes || '').trim(),
              activityThemeKey: primaryActivityTheme.key,
              activityThemeColor: primaryActivityTheme.color,
              activityThemeLabel: primaryActivityTheme.label,
              categoryId: 'activity',
              categoryLabel: 'Activity',
              categoryColor: primaryActivityTheme.color,
              activityTiming: {
                mode: 'selected',
                recordedAt: resolvedTimestamp.toISOString(),
                durationMinutes: null,
              },
              localDate: toLocalDateKey(resolvedTimestamp),
              localTime: toLocalTimeKey(resolvedTimestamp),
              timestampSource: 'local-input',
            }
          : formData;
        const entryData = {
          childId: child.id,
          actionType,
          data: resolvedActivityData,
          timestamp: resolvedTimestamp,
          completedBy: 'current_user', // TODO: Get from auth context
        };

        if (isEditMode && (entryId || initialData?.id)) {
          const targetId = entryId || initialData?.id;
          if (actionType === 'activity') {
            await updateDoc(doc(db, 'dailyLogs', targetId), {
              childId: child.id,
              category: 'activity',
              title: 'Activity',
              titlePrefix: 'Activity',
              text: resolvedActivityData.notes || null,
              timestamp: resolvedTimestamp,
              status: 'active',
              localDate: resolvedActivityData.localDate,
              localTime: resolvedActivityData.localTime,
              activityDetails: resolvedActivityData,
              updatedAt: serverTimestamp(),
            });

            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
                detail: {
                  childId: child.id,
                  collection: 'dailyLogs',
                  entryId: targetId,
                },
              }));
            }

            onComplete?.(actionType, {
              ...entryData,
              id: targetId,
              collection: 'dailyLogs',
              category: 'activity',
              title: 'Activity',
              titlePrefix: 'Activity',
              text: resolvedActivityData.notes || null,
              activityDetails: resolvedActivityData,
            });
            onClose();
            return entryData;
          }

          await updateDoc(doc(db, 'dailyCare', targetId), {
            childId: child.id,
            actionType,
            data: entryData.data,
            completedBy: entryData.completedBy,
            timestamp: resolvedTimestamp,
            date: resolvedTimestamp.toDateString(),
            status: 'active',
            updatedAt: serverTimestamp(),
          });

          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
              detail: {
                childId: child.id,
                collection: 'dailyCare',
                entryId: targetId,
              },
            }));
          }

          onComplete?.(actionType, { ...entryData, id: targetId });
          onClose();
          return entryData;
        }

        if (actionType === 'activity') {
          const auth = getAuth();
          const currentUser = auth.currentUser;
          if (!currentUser) {
            throw new Error('User must be authenticated to save activity entries');
          }

          const activityLogEntry = {
            childId: child.id,
            createdBy: currentUser.uid,
            createdAt: serverTimestamp(),
            source: {
              type: 'manual',
              label: 'Manual',
            },
            category: 'activity',
            title: 'Activity',
            titlePrefix: 'Activity',
            text: resolvedActivityData.notes || null,
            timestamp: resolvedTimestamp,
            status: 'active',
            localDate: resolvedActivityData.localDate,
            localTime: resolvedActivityData.localTime,
            activityDetails: resolvedActivityData,
            tags: ['activity', resolvedActivityData.activityType].filter(Boolean),
          };

          const docRef = await addDoc(collection(db, 'dailyLogs'), activityLogEntry);

          if (typeof window !== 'undefined') {
            const eventDetail = {
              id: docRef.id,
              ...activityLogEntry,
              collection: 'dailyLogs',
            };
            window.dispatchEvent(new CustomEvent('captureez:timeline-entry-created', {
              detail: eventDetail,
            }));
            window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
              detail: {
                childId: child.id,
                collection: 'dailyLogs',
                entryId: docRef.id,
              },
            }));
          }

          return {
            ...entryData,
            id: docRef.id,
            collection: 'dailyLogs',
            category: 'activity',
            title: 'Activity',
            titlePrefix: 'Activity',
            text: resolvedActivityData.notes || null,
            activityDetails: resolvedActivityData,
          };
        }

        await saveDailyCareEntry(entryData);
        return entryData;
      },
      { formData }
    );
  };

  const handleNext = () => {
    if (isLastStep) {
      handleSave();
      return;
    }

    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const renderField = (field) => {
    const value = formData[field.key];
    const primaryActivityTheme = getPrimaryActivityTheme(formData.activityTypes || []);

    switch (field.type) {
      case 'scale':
        if (isSinglePageFlow && field.key === 'engagement') {
          const selectedValue = engagementValue ?? (value ?? null);
          const selectedActivityTheme = primaryActivityTheme;
          return (
            <Box sx={{ mt: 0.65, pb: 0.1 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                  gap: 0.6,
                  width: '100%',
                }}
              >
                {ACTIVITY_ENGAGEMENT_OPTIONS.map((option) => {
                  const selected = Number(selectedValue) === option.value;
                  return (
                    <ButtonBase
                      key={option.value}
                      onClick={() => handleFieldChange(field.key, option.value)}
                      aria-pressed={selected}
                      component="button"
                      type="button"
                      sx={{
                        minWidth: 0,
                        minHeight: 54,
                        px: 0.6,
                        py: 0.75,
                        borderRadius: '18px',
                        border: '1px solid',
                        borderColor: selected ? selectedActivityTheme.color : '#E2E8F0',
                        bgcolor: selected ? selectedActivityTheme.softBg : '#FFFFFF',
                        color: selected ? selectedActivityTheme.dark : '#64748B',
                        textTransform: 'none',
                        appearance: 'none',
                        cursor: 'pointer',
                        boxShadow: selected ? `0 0 0 1px ${selectedActivityTheme.color} inset` : 'none',
                        transition: 'background-color 180ms ease, color 180ms ease, box-shadow 180ms ease, transform 140ms ease',
                        '&:hover': {
                          bgcolor: selected ? selectedActivityTheme.softBg : '#F8FAFC',
                          transform: 'translateY(-1px)',
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${selectedActivityTheme.color}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.25, width: '100%', minWidth: 0 }}>
                        <Box component="span" sx={{ display: 'grid', placeItems: 'center', lineHeight: 1, opacity: 0.85 }}>
                          {renderOptionVisual(option, { iconSize: 15, emojiFontSize: '0.82rem' })}
                        </Box>
                        <Typography component="span" sx={{ fontSize: option.value === 5 ? '0.71rem' : '0.77rem', fontWeight: selected ? 700 : 600, lineHeight: 1.05, textAlign: 'center' }}>
                          {option.label}
                        </Typography>
                      </Box>
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>
          );
        }

        return (
          <Box sx={{ px: 2, mt: 3 }}>
            <Slider
              value={value || field.defaultValue || 3}
              onChange={(_, newValue) => handleFieldChange(field.key, newValue)}
              min={field.min}
              max={field.max}
              step={1}
              marks={field.labels?.map((label, index) => ({
                value: index + 1,
                label: label.split(' ')[0]
              }))}
              sx={{ 
                mt: 4, 
                mb: 2,
                color: theme.palette.dailyCare.primary,
                '& .MuiSlider-thumb': {
                  bgcolor: theme.palette.dailyCare.primary,
                },
                '& .MuiSlider-track': {
                  bgcolor: theme.palette.dailyCare.primary,
                },
                '& .MuiSlider-rail': {
                  bgcolor: alpha('#6D28D9', 0.2),
                },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontSize: '1.1rem' }}>
              {field.labels?.[value - 1] || 'Select a value'}
            </Typography>
          </Box>
        );

      case 'chips':
        if (isSinglePageFlow && field.key === 'activityTypes') {
          return (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
              gap: 0.75,
              mt: 1,
            }}>
              {ACTIVITY_TYPE_OPTIONS.map((option) => {
                const isSelected = field.multiple
                  ? value?.includes(option.value)
                  : value === option.value;
                const activityTheme = getActivityTheme(option.themeKey || option.value);

                return (
                  <ButtonBase
                    key={option.value}
                    component="button"
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => {
                      if (field.multiple) {
                        const current = value || [];
                        const newValue = isSelected
                          ? current.filter((v) => v !== option.value)
                          : [...current, option.value];
                        handleFieldChange(field.key, newValue);
                      } else {
                        handleFieldChange(field.key, option.value);
                      }
                    }}
                    sx={{
                      minHeight: 40,
                      px: 0.8,
                      py: 0.6,
                      borderRadius: '14px',
                      border: '1px solid',
                      borderColor: isSelected ? activityTheme.color : '#E2E8F0',
                      bgcolor: isSelected ? activityTheme.softBg : '#FFFFFF',
                      color: isSelected ? activityTheme.dark : '#64748B',
                      textTransform: 'none',
                      appearance: 'none',
                      cursor: 'pointer',
                      boxShadow: isSelected ? `inset 0 0 0 1px ${activityTheme.color}` : 'none',
                      transition: 'background-color 180ms ease, color 180ms ease, border-color 180ms ease, transform 140ms ease, box-shadow 180ms ease',
                      '&:hover': {
                        bgcolor: isSelected ? activityTheme.softBg : '#F8FAFC',
                        borderColor: isSelected ? activityTheme.color : '#CBD5E1',
                        transform: 'translateY(-1px)',
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${activityTheme.color}`,
                        outlineOffset: 2,
                      },
                    }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.55, width: '100%', minWidth: 0 }}>
                      {renderOptionVisual(option)}
                      <Typography component="span" sx={{ fontSize: '0.84rem', fontWeight: isSelected ? 700 : 600, lineHeight: 1.05, textAlign: 'center' }}>
                        {option.label}
                      </Typography>
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          );
        }

        return (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
            gap: 0.75,
            mt: 1,
          }}>
            {field.options?.map((option) => {
              const isSelected = field.multiple
                ? value?.includes(option.value)
                : value === option.value;

              return (
                <Chip
                  key={option.value}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, width: '100%' }}>
                      {renderOptionVisual(option, { iconSize: 16, emojiFontSize: '0.95rem' })}
                      <Typography component="span" sx={{ fontSize: '0.84rem', fontWeight: isSelected ? 700 : 600, lineHeight: 1, textAlign: 'center' }}>
                        {option.label}
                      </Typography>
                    </Box>
                  }
                  variant={isSelected ? 'filled' : 'outlined'}
                  clickable
                  sx={{
                    height: 40,
                    width: '100%',
                    borderRadius: '14px',
                    px: 0.75,
                    py: 0,
                    color: isSelected ? 'white' : theme.palette.dailyCare.primary,
                    bgcolor: isSelected ? theme.palette.dailyCare.primary : 'transparent',
                    borderColor: theme.palette.dailyCare.primary,
                    '& .MuiChip-label': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      px: 0,
                    },
                    '&:hover': { 
                      transform: 'scale(1.05)',
                      bgcolor: isSelected ? theme.palette.dailyCare.hover : theme.palette.dailyCare.background,
                    }
                  }}
                  onClick={() => {
                    if (field.multiple) {
                      const current = value || [];
                      const newValue = isSelected
                        ? current.filter(v => v !== option.value)
                        : [...current, option.value];
                      handleFieldChange(field.key, newValue);
                    } else {
                      handleFieldChange(field.key, option.value);
                    }
                  }}
                />
              );
            })}
          </Box>
        );

      case 'text':
        if (isSinglePageFlow && actionType === 'activity' && field.key === 'notes') {
          return (
            <SimpleNotesInput
              hintText="Tip: use your phone mic to dictate notes."
              value={value || ''}
              onDataChange={(data) => handleFieldChange(field.key, data?.text || '')}
              placeholder={field.placeholder}
              minRows={field.rows || 3}
            />
          );
        }

        return (
          <TextField
            fullWidth
            multiline={field.multiline}
            rows={field.rows || 3}
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            sx={{ mt: 2 }}
          />
        );

      case 'time':
        return (
          <LogTimeField
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            containerSx={{ mt: 2 }}
          />
        );

      case 'number':
        return (
          <TextField
            type="number"
            fullWidth
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            inputProps={{ min: field.min, max: field.max }}
            sx={{ mt: 2 }}
          />
        );

      case 'display': {
        return (
          <Box
            sx={{
              bgcolor: 'primary.light',
              borderRadius: 2,
              p: 1.5,
              textAlign: 'center',
              mt: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark' }}>
              Complete the required fields to continue.
            </Typography>
          </Box>
        );
      }

      default:
        return null;
    }
  };

  const actionFooter = isSinglePageFlow ? (
    <StyledButton
      onClick={handleSave}
      disabled={!canProceed() || careForm.loading}
      endIcon={<CheckIcon />}
      sx={{
        bgcolor: !canProceed() || careForm.loading ? '#F1F5F9' : '#059669',
        color: !canProceed() || careForm.loading ? '#94A3B8' : '#FFFFFF',
        borderRadius: '0.9rem',
        boxShadow: !canProceed() || careForm.loading ? 'none' : '0 1px 3px rgba(15, 23, 42, 0.08)',
        minWidth: '100%',
        width: '100%',
        border: '1px solid',
        borderColor: !canProceed() || careForm.loading ? '#E2E8F0' : '#059669',
        '&:hover': {
          bgcolor: !canProceed() || careForm.loading ? '#F1F5F9' : '#047857',
          borderColor: !canProceed() || careForm.loading ? '#E2E8F0' : '#047857',
        },
        '&:disabled': {
          bgcolor: '#F1F5F9',
          color: '#94A3B8',
          borderColor: '#E2E8F0',
          boxShadow: 'none',
        }
      }}
    >
      {careForm.loading ? 'Saving...' : 'Save Activity'}
    </StyledButton>
  ) : null;

  if (isSinglePageFlow) {
    return (
      <LogFormShell
        open={open}
        onClose={handleClose}
        title={config.title}
        subtitle={config.description}
        footer={actionFooter}
        forceDrawer={true}
      >
        {careForm.error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }} 
            onClose={() => careForm.clearError()}
          >
            {careForm.error}
          </Alert>
        )}

        {actionType === 'activity' ? (
          <Box sx={{ mb: 0.5 }}>
            <DateTimeSection
              value={activityTimestamp}
              onChange={setActivityTimestamp}
            />
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.35 }}>
          {fieldsToRender.map((field, index) => (
            <Fade in={true} key={field.key || index}>
              <Box sx={{ minHeight: 'auto' }}>
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 0.45, fontWeight: 600, fontSize: '0.98rem', color: 'text.primary' }}>
                    {field.label}
                  </FormLabel>
                  {field.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontSize: '0.9rem' }}>
                      {field.description}
                    </Typography>
                  )}

                  {renderField(field)}
                </FormControl>
              </Box>
            </Fade>
          ))}
        </Box>
      </LogFormShell>
    );
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: 500,
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: 24,
        p: 4,
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {config.iconComponent ? (
              <config.iconComponent sx={{ fontSize: 24, color: theme.palette.dailyCare.primary }} />
            ) : (
              <Typography sx={{ fontSize: '1.5rem' }}>{config.icon}</Typography>
            )}
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.dailyCare.primary }}>
              {config.title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={careForm.loading}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Progress */}
        {!isSinglePageFlow && config.fields.length > 1 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Step {currentStep + 1} of {config.fields.length} • {child.name}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                mt: 0.5, 
                height: 6, 
                borderRadius: 3,
                bgcolor: theme.palette.dailyCare.background,
                '& .MuiLinearProgress-bar': {
                  bgcolor: theme.palette.dailyCare.primary,
                }
              }}
            />
          </Box>
        )}

        {/* Error Alert */}
        {careForm.error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }} 
            onClose={() => careForm.clearError()}
          >
            {careForm.error}
          </Alert>
        )}

        {/* Current Field */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isSinglePageFlow ? 1.35 : 0 }}>
          {fieldsToRender.map((field, index) => (
            <Fade in={true} key={field.key || index}>
              <Box sx={{ minHeight: isSinglePageFlow ? 'auto' : 200 }}>
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: isSinglePageFlow ? 0.45 : 1, fontWeight: 600, fontSize: isSinglePageFlow ? '0.98rem' : '1.1rem', color: 'text.primary' }}>
                    {field.label}
                  </FormLabel>
                  {field.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: isSinglePageFlow ? 0.75 : 2, fontSize: isSinglePageFlow ? '0.9rem' : '0.875rem' }}>
                      {field.description}
                    </Typography>
                  )}

                  {renderField(field)}
                </FormControl>
              </Box>
            </Fade>
          ))}
        </Box>

        {/* Navigation */}
        {isSinglePageFlow ? (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3 }}>
              <StyledButton
                variant="contained"
                onClick={handleSave}
                disabled={!canProceed() || careForm.loading}
                endIcon={<CheckIcon />}
                sx={{
                  bgcolor: !canProceed() || careForm.loading ? '#F1F5F9' : '#059669',
                  color: !canProceed() || careForm.loading ? '#94A3B8' : '#FFFFFF',
                  borderRadius: '0.75rem',
                  boxShadow: !canProceed() || careForm.loading ? 'none' : '0 1px 3px rgba(15, 23, 42, 0.08)',
                  minWidth: isMobile ? '100%' : 180,
                  border: '1px solid',
                  borderColor: !canProceed() || careForm.loading ? '#E2E8F0' : '#059669',
                  '&:hover': {
                    bgcolor: !canProceed() || careForm.loading ? '#F1F5F9' : '#047857',
                    borderColor: !canProceed() || careForm.loading ? '#E2E8F0' : '#047857',
                  },
                  '&:disabled': {
                    bgcolor: '#F1F5F9',
                    color: '#94A3B8',
                    borderColor: '#E2E8F0',
                    boxShadow: 'none',
                  }
                }}
              >
              {careForm.loading ? 'Saving...' : 'Save Activity'}
            </StyledButton>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
              <StyledButton
                disabled={currentStep === 0 || careForm.loading}
                onClick={handleBack}
              >
                Back
              </StyledButton>

              <Typography variant="caption" color="text.secondary">
                {currentField.required ? 'Required' : 'Optional'}
              </Typography>
              
              <StyledButton
                variant="contained"
                onClick={handleNext}
                disabled={!canProceed() || careForm.loading}
                endIcon={isLastStep ? <CheckIcon /> : null}
                sx={{
                  bgcolor: '#6D28D9',
                  '&:hover': {
                    bgcolor: theme.palette.dailyCare.hover,
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500'
                  }
                }}
              >
                {careForm.loading ? 'Saving...' : isLastStep ? 'Complete' : 'Next'}
              </StyledButton>
            </Box>

            {/* Skip option for optional fields */}
            {!currentField.required && !canProceed() && (
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Button variant="text" size="small" onClick={handleNext} disabled={careForm.loading}>
                  Skip this step
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default DailyCareModal;
